import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useOfflineCompat } from "@/composables/useOfflineCompat";
import { useRest } from "@/composables/useRest";

export const useMonedasStore = defineStore("monedas", () => {
  // ── Catálogo (usado por AfectacionForm) ──────────────────────────────────
  const monedas = ref([]);
  const monedasListLoad = ref(false);

  const offline = useOfflineCompat();
  const rest = useRest();

  const BASE_URL = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/tipo-moneda`;

  async function setMonedas(refresh = false) {
    try {
      let datos = [];
      if (monedasListLoad.value && !refresh) return;

      if (!refresh && offline.offlineServer) {
        let r = await offline.getList("tipoMoneda");
        if (Array.isArray(r.data.data) && r.data.data.length > 0 && r.data.data[0].list) {
          datos = r.data.data[0].list;
        }
      }

      if (!datos || refresh || datos.length < 1) {
        const r = await rest.get({ url: BASE_URL });
        datos = r.data.data;
        offline.setList("tipoMoneda", datos);
      }

      monedas.value = datos;
      monedasListLoad.value = true;
    } catch (e) {
      console.error("setMonedas>>", e);
    }
  }

  // ── Parametrización CRUD con paginación ─────────────────────────────────
  const tipoMonedas = ref([]);
  const allTipoMonedas = ref([]);
  const allTipoMonedasLoaded = ref(false);
  const catalogNeedsRefresh = ref(false);
  const isLoading = ref(false);
  const pageSize = ref(6);
  const currentPage = ref(1);
  const totalTipoMonedas = ref(0);

  const offset = computed(() => (currentPage.value - 1) * pageSize.value);
  const totalPages = computed(() => Math.ceil(totalTipoMonedas.value / pageSize.value));

  async function getTotal(searchQuery = null) {
    try {
      let url = `${BASE_URL}?getTotal=true`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const { data } = await rest.get({ url });
      totalTipoMonedas.value = +data.doc.total;
    } catch (e) {
      console.error("getTotal>>", e);
    }
  }

  async function setAllTipoMonedas(refresh = false) {
    try {
      if (allTipoMonedasLoaded.value && !refresh && !catalogNeedsRefresh.value) {
        return allTipoMonedas.value;
      }
      const { data } = await rest.get({ url: BASE_URL });
      allTipoMonedas.value = data.data;
      allTipoMonedasLoaded.value = true;
      catalogNeedsRefresh.value = false;
      return allTipoMonedas.value;
    } catch (e) {
      console.error("setAllTipoMonedas>>", e);
      return allTipoMonedas.value;
    }
  }

  async function setTipoMonedas(page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;
      const url = `${BASE_URL}?limit=${pageSize.value}&offset=${(page - 1) * pageSize.value}`;
      const { data } = await rest.get({ url });
      tipoMonedas.value = data.data;
    } catch (e) {
      console.error("setTipoMonedas>>", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function searchTipoMonedas(searchQuery, page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;
      const url = `${BASE_URL}?limit=${pageSize.value}&offset=${(page - 1) * pageSize.value}&search=${encodeURIComponent(searchQuery)}`;
      const { data } = await rest.get({ url });
      tipoMonedas.value = data.data;
    } catch (e) {
      console.error("searchTipoMonedas>>", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function createTipoMoneda(payload) {
    try {
      const { data } = await rest.post({ url: BASE_URL, formData: payload });
      allTipoMonedasLoaded.value = false;
      monedasListLoad.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("createTipoMoneda>>", e);
      throw e;
    }
  }

  async function updateTipoMoneda(id, payload) {
    try {
      const { data } = await rest.put({ url: `${BASE_URL}/${id}`, formData: payload });
      allTipoMonedasLoaded.value = false;
      monedasListLoad.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("updateTipoMoneda>>", e);
      throw e;
    }
  }

  async function deleteTipoMoneda(id) {
    try {
      const { data } = await rest.del({ url: `${BASE_URL}/${id}` });
      allTipoMonedasLoaded.value = false;
      monedasListLoad.value = false;
      catalogNeedsRefresh.value = true;
      await getTotal();
      await setTipoMonedas(currentPage.value);
      return data;
    } catch (e) {
      console.error("deleteTipoMoneda>>", e);
      throw e;
    }
  }

  function handlePageChange(event) {
    goToPage(Math.floor(event.first / pageSize.value) + 1);
  }

  function goToPage(page) {
    currentPage.value = page;
    setTipoMonedas(page);
  }

  function prevPage() {
    if (currentPage.value > 1) goToPage(currentPage.value - 1);
  }

  function nextPage() {
    if (currentPage.value < totalPages.value) goToPage(currentPage.value + 1);
  }

  return {
    // Catálogo
    monedas,
    monedasListLoad,
    setMonedas,
    // Parametrización
    tipoMonedas,
    allTipoMonedas,
    isLoading,
    pageSize,
    currentPage,
    offset,
    totalTipoMonedas,
    totalPages,
    setAllTipoMonedas,
    setTipoMonedas,
    searchTipoMonedas,
    getTotal,
    createTipoMoneda,
    updateTipoMoneda,
    deleteTipoMoneda,
    handlePageChange,
    goToPage,
    prevPage,
    nextPage,
  };
});
